import { Getter, Setter, atom } from "jotai";
import { Editor, Box, TLShapeId } from "tldraw";
import type { SlideSummary } from "@/services/api";

import type { WorkInfo, ChatMessagesItem, ClassOverInfo } from "@/services/api";
import { asyncGetAudio } from "@/services/textToSpeech";

// 导入整合的TTS状态
// 这些atom现在从ttsAtom.ts导入，保持单一数据源
import {
  voiceDataAtom as ttsVoiceDataAtom,
  playTasksAtom as ttsPlayTasksAtom,
  currentPlayingAtom as ttsCurrentPlayingAtom,
  isVoiceAtom as ttsIsVoiceAtom,
  voiceConfigAtom as ttsVoiceConfigAtom,
  voiceRatioAtom as ttsVoiceRatioAtom,
  ttsPlayerAtom
} from "./ttsAtom";

interface ChatMessagesListOpts {
  type?: "init" | "add" | "updateLast" | "clear";
  list?: ChatMessagesItem[];
  item?: ChatMessagesItem;
}
interface DalleData {
  url: string;
  sid: string;
}
export interface SlideInfo {
  slideId: string;
  sid?: string;
  taskId?: number;
}

interface EditorData {
  action?: "create" | "modify" | "delete";
  shape?: any;
  end?: boolean;
  sid?: string;
  firstShapeId?: string;
  msgSid?: string;
  firstBlock?: boolean;
}

interface SelectionBounds {
  bounds: Box;
  shapeId: TLShapeId;
}

export interface CoderData {
  text?: string;
  status?: "add" | "loading" | "done";
  sid?: string;
}

export enum BTN_STATUS {
  DISABLED = 0, // 禁用
  WAITING = 1,
  RECORDING = 2,
  SEND = 3,
}
export enum AvatarStatusEnum {
  WAITING = "waiting",
  THINKING = "thinking",
  SPEAKING = "speaking",
  LISTENING = "listening",
}

export const workIdAtom = atom<string>("");
export const editorAtom = atom<Editor | null>(null);
export const showStylePanelAtom = atom<boolean>(false);
export const keepOpenStylePanelAtom = atom<boolean>(false);
export const workDataAtom = atom<WorkInfo | null>(null);
export const classOverInfoAtom = atom<ClassOverInfo | null>(null);
export const workNameAtom = atom<string>("");
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceDataAtom = ttsVoiceDataAtom;
export const playTasksAtom = ttsPlayTasksAtom;
export const messageCompleteSidsAtom = atom<any>({});
export const avatarStatusAtom = atom<AvatarStatusEnum>(
  AvatarStatusEnum.WAITING
);
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const currentPlayingAtom = ttsCurrentPlayingAtom;
export const hasPlayedHistoryAtom = atom<boolean>(false);

// 自拼和板书不支持题目和文本合并菜单形式
export const isPracticeCourseAtom = atom<boolean>(
  (get) =>
    get(workDataAtom)?.seriesId !== 4 && get(workDataAtom)?.seriesId !== 15
);

// 保存流式消息是否推送完成状态
export const isCompleteSidAtom = atom<any>({});

export enum LoadingType {
  /* 加载中 */
  Loading = "loading",
  /* 错误 */
  Error = "error",
  /* ws 完成连接 */
  Conencted = "conencted",
  /* 画板加载完成 */
  DrawBoardLoaded = "drawboardLoaded",
  /* 画板和 ws 都已完成*/
  AllReady = "ready",
}
// 0: loading 1: conected 1: success -1: error
export const loadingStatusAtom = atom<LoadingType>(LoadingType.Loading);
// 用户是否点击去上课
export const userClickedAtom = atom<boolean>(false);
// 是否准备好开始上课
export const readyStartClassAtom = atom<boolean>(
  (get) =>
    get(userClickedAtom) && get(loadingStatusAtom) === LoadingType.AllReady
);
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceConfigAtom = ttsVoiceConfigAtom;
// 导出整合的TTS播放器状态，供新代码使用
export { ttsPlayerAtom };

const delay = (ms?: number) => {
  return new Promise((resolve) => setTimeout(resolve, (ms || 0) * 1000));
};

const setVoiceAndPlay = async (
  get: Getter,
  set: Setter,
  messageItem: ChatMessagesItem
) => {
  await delay(messageItem.audioIndex);

  const resAudio = await asyncGetAudio(
    messageItem.message,
    get(voiceConfigAtom),
    messageItem.language,
    get(voiceRatioAtom),
    get(workDataAtom)?.seriesId
  );
  const voiceData = { ...get(voiceDataAtom) };
  const currentItem = { ...(voiceData[messageItem.sid!] || { voiceList: [] }) };
  currentItem.duration = currentItem.duration
    ? currentItem.duration + parseInt(resAudio?.addition?.duration, 10)
    : parseInt(resAudio?.addition?.duration, 10);
  if (!currentItem.voiceList) {
    currentItem.voiceList = [];
  }
  currentItem.voiceList[messageItem.audioIndex!] = {
    ...resAudio,
    ...messageItem,
  };
  set(voiceDataAtom, {
    ...voiceData,
    [messageItem.sid!]: currentItem,
  });
};

const readChatMessagesListAtom = atom<ChatMessagesItem[]>([]);
export const chatMessagesListAtom = atom(
  (get) => get(readChatMessagesListAtom),
  (get: Getter, set: Setter, opts: ChatMessagesListOpts) => {
    const isVoice = get(isVoiceAtom);
    const playTasks = [...get(playTasksAtom)];
    const list: ChatMessagesItem[] = [...get(readChatMessagesListAtom)];
    const loading = get(loadingAtom);
    if (opts.type === "init" && opts.list) {
      const tmpList = [...opts.list].reverse();
      set(readChatMessagesListAtom, list.concat(tmpList));
    } else if (opts.type === "add" && opts.item) {
      if (opts.item.name === "slide") {
        const item = list.find((item) => item.sid === opts.item?.sid);
        if (item) {
          set(
            readChatMessagesListAtom,
            list.map((itemMsg) => {
              if (itemMsg.sid === opts.item?.sid) {
                return {
                  ...itemMsg,
                  resources: opts.item?.resources,
                };
              }
              return itemMsg;
            })
          );
        } else {
          list.unshift({ ...opts.item });
          set(readChatMessagesListAtom, list);
        }
      } else {
        list.unshift(opts.item);
        set(readChatMessagesListAtom, list);
      }
    } else if (opts.type === "updateLast" && opts.item?.message) {
      // 收到文字回复去除 loading 效果
      if (loading && !loading.type) {
        set(loadingAtom, null);
      }
      const { message, sid, audioIndex } = opts.item;
      let newMessage = message + "\n";

      const item = list.find((item) => item.sid === sid);
      if (!item || (item && item.name === "slide")) {
        let tmpItem;
        if (item && item.name === "slide") {
          tmpItem = list.splice(0, 1)[0];
        }
        list.unshift({
          name: "teacher",
          content: newMessage + "\n",
          isVoice,
          sid,
          slideId: get(slideIdAtom),
          taskId: get(slideInfoAtom)?.taskId,
          resources: tmpItem?.resources,
        });

        if (isVoice) {
          set(voiceDataAtom, {
            ...get(voiceDataAtom),
            [sid!]: {
              voiceList: [],
              length: 1,
              slideId: get(slideIdAtom),
              taskId: get(slideInfoAtom)?.taskId,
            },
          });
          playTasks.push(sid!);
          set(playTasksAtom, playTasks);
          setVoiceAndPlay(get, set, opts.item);
        }
      } else {
        if (item) {
          item.content += newMessage;
          item.content = item.content?.replace(/\\n/g, "\n");
          item.content = item.content + "\n";

          if (isVoice && sid) {
            const data = get(voiceDataAtom)?.[sid];
            set(voiceDataAtom, {
              ...get(voiceDataAtom),
              [sid]: {
                ...data,
                length:
                  (data.length || 0) <= audioIndex! + 1
                    ? audioIndex! + 1
                    : data.length,
              },
            });
            setVoiceAndPlay(get, set, opts.item);
          }
        }
      }
      set(readChatMessagesListAtom, list);
    } else if (opts.type === "clear") {
      set(readChatMessagesListAtom, []);
    }
    return list;
  }
);

export const btnStatusAtom = atom(BTN_STATUS.DISABLED);

export const possibleResponsesAtom = atom<string[]>([]);

// 已选择 shape 位置
export const selectionBoundsAtom = atom<SelectionBounds | null>(null);
//
export const slideInfoAtom = atom<SlideInfo | null>(null);
export const slideIdAtom = atom((get) => get(slideInfoAtom)?.slideId);
// 画板是否修改
export const isUpdateDrawboardAtom = atom<boolean>(false);

// 生图 loading
export const loadingAtom = atom<{
  type?: "coder_loading" | "dalle_loading" | "editor_loading";
  status: boolean;
  slideId?: string;
} | null>(null);

// 生成图片数据
export const dalleDataAtom = atom<DalleData | null>(null);
// 生成代码数据
export const readCoderDataAtom = atom<CoderData | null>(null);
export const coderDataAtom = atom(
  (get) => get(readCoderDataAtom),
  (get: Getter, set: Setter, data: CoderData | null) => {
    const { text: prevText = "", status: prevStatus }: CoderData =
      get(coderDataAtom) || {};
    const { status, text, sid } = data || {};
    if (status === "loading") {
      set(readCoderDataAtom, {
        status: !prevStatus ? "add" : "loading",
        text: prevText + text,
        sid,
      });
    } else if (status === "done" && text) {
      set(readCoderDataAtom, {
        status: "done",
        text,
        sid,
      });
    } else {
      set(readCoderDataAtom, data);
    }
  }
);

let queueText: any[] = [];
let runningShape = false;

export const writingShapeAtom = atom<any>(null);
export const shapeTextBlockLoadingAtom = atom(false, (get, set, value) => {
  const writingShapeList = get(writingShapeListAtom);
  if (!value && !queueText.length && writingShapeList?.length > 0) {
    set(shapeCacheListAtom, {
      action: "updateLast",
      data: get(writingShapeListAtom).map((item: any) =>
        get(editorAtom)?.getShape(item)
      ),
    });
    set(writingShapeListAtom, []);
  }
  set(shapeTextBlockLoadingAtom, value);
});

const runQueue = (get: any, set: any) => {
  if (queueText.length > 0) {
    typedEditor(get, set, queueText[0]);
  }
};

const typedEditor = (get: any, set: any, data: any) => {
  set(writingShapeAtom, {
    id: data.shape?.shapeId || data.shape?.id,
    note: data.shape?.meta?.note,
  });
  let index = 1;
  const text = data.shape.props?.text;
  const tmpData = { ...data };
  const timer = setInterval(() => {
    index++;
    tmpData.shape.props.text = text.slice(0, index);
    tmpData.shape.isLocked = !(index >= text.length);
    set(editorDataAtom, { ...tmpData, textEnd: index >= text.length });

    if (index >= text.length) {
      clearInterval(timer);
      queueText.shift();
      if (queueText.length > 0) {
        runQueue(get, set); // 继续下一个
      } else {
        runningShape = false;
        set(writingShapeAtom, null);
        // 所有文字都写完了
        if (!get(shapeTextBlockLoadingAtom)) {
          setTimeout(() => {
            set(shapeCacheListAtom, {
              action: "updateLast",
              data: get(writingShapeListAtom).map((item: any) =>
                get(editorAtom).getShape(item)
              ),
            });
            set(writingShapeListAtom, []);
          }, 100);
        }
      }
    }
  }, 35);
  runningShape = true;
};

// 生成画板数据
export const editorDataAtom = atom<
  EditorData | null,
  [EditorData | null],
  void
>(null, (get, set, opts: EditorData | null) => {
  const { msgSid, ...data } = opts || {};
  const playedSidRef = get(playedSidRefAtom);
  const pendingEditorMap = { ...get(pendingEditorMapAtom) };
  if (Array.isArray(opts)) {
    set(editorDataAtom, opts);
    return;
  }
  if (!msgSid || playedSidRef[msgSid]) {
    if (
      data?.shape?.type === "text" &&
      data?.action === "create" &&
      get(slideIdAtom)
    ) {
      // 第一次创建文字块，缓存到 shapeCacheListAtom 占位
      if (data?.firstBlock) {
        set(shapeCacheListAtom, {
          data: [{...data.shape, isPlaceholder: true}],
        });
      }
      queueText.push(data);
      if (runningShape) {
        return;
      }
      runQueue(get, set);
      return;
    }
    set(editorDataAtom, opts && data ? data : null);
  } else {
    // 未播放，添加到待处理队列（支持多个）
    const list = pendingEditorMap[msgSid] || [];
    list.push(data);
    set(pendingEditorMapAtom, {
      ...pendingEditorMap,
      [msgSid]: list,
    });
  }
});
export const pendingEditorMapAtom = atom<any>({});
export const playedSidRefAtom = atom<any, [any], void>({}, (get, set, opts) => {
  const pendingEditorMap = { ...get(pendingEditorMapAtom) };
  set(playedSidRefAtom, {
    ...get(playedSidRefAtom),
    [opts?.sid!]: true,
  });
  const pendingList = pendingEditorMap[opts?.sid!];
  if (pendingList && pendingList.length) {
    // 按顺序依次触发处理
    set(editorDataAtom, pendingList);
    delete pendingEditorMap[opts?.sid!];
    set(pendingEditorMapAtom, pendingEditorMap);
  }
});

// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const isVoiceAtom = ttsIsVoiceAtom;

export const remainingRoundsAtom = atom<number>(0);

export const totalRoundsAtom = atom<number>(0);

export const reportDataAtom = atom<SlideSummary | null>();

export const isChatBoxOpenAtom = atom<boolean>(false);

export const isCourseSettingsOpenAtom = atom<boolean>(false);

export const isWorkLeftPanelOpenAtom = atom<boolean>(false);

export const todayWorksAtom = atom<any>([]);

export const earlierWorksAtom = atom<any>([]);

export const weekWorksAtom = atom<any>([]);

export const topWorksAtom = atom<any>([]);

export const teacherSelectedAtom = atom<any>(null);

export const selectedBgAtom = atom<any>(null);

export const backgroundsAtom = atom<any>(null);

export const workSelectedAtom = atom<any>(null);

export const barrageAtom = atom<any>("");

export const captionAtom = atom<any>("");

export const teachersAtom = atom<any>([]);

// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceRatioAtom = ttsVoiceRatioAtom;

export const recentLessonAtom = atom<Array<any>>([]);

export const chatShapesAtom = atom<Record<TLShapeId, any>>({});
export const chatTextListAtom = atom<Set<string>>(new Set([]));

export const userMessageClearingAtom = atom<boolean>(false);

export const newChatCreatingAtom = atom<boolean>(false);

export const studyTimeAtom = atom<number>(0);
export const studyStopAtom = atom<boolean>(false);

export const aiShapeTextDataAtom = atom({}, (get, set, params: any) => {
  const data: any = { ...get(aiShapeTextDataAtom) };
  const slideId = get(slideIdAtom);
  const editor = get(editorAtom);
  if (params.action === "init") {
    set(aiShapeTextDataAtom, params.data);
    return;
  }
  if (!slideId) {
    return;
  }
  if (data[slideId]) {
    if (params.firstBlock) {
      editor?.deleteShapes(
        data[slideId].map((item: any) => item.shapeId || item.id)
      );
    }
    data[slideId].push(params.data);
  } else {
    data[slideId] = [params.data];
  }
  set(aiShapeTextDataAtom, data);
});

// ai 生成的题目&文本 shape 缓存列表
export const shapeCacheListAtom = atom({}, (get, set, params: any) => {
  const data: any = { ...get(shapeCacheListAtom) };
  const slideId = get(slideIdAtom);
  if (params.action === "init") {
    set(shapeCacheListAtom, params.data);
    return;
  }
  if (!slideId) {
    return;
  }
  if (data[slideId]) {
    if (params.action === "updateLast") {
      data[slideId][data[slideId].length - 1] = params.data;
    } else {
      data[slideId].push(params.data);
    }
  } else {
    data[slideId] = [params.data];
  }
  // 长度-1 为当前选中的导航序号
  set(shapeCacheListNumAtom, data[slideId].length - 1);
  set(shapeCacheListAtom, data);
});
// 选中导航序号
export const shapeCacheListNumAtom = atom<number>(0);

export const textShapePageAtom = atom<{ page: number; slideId: string }>({
  page: 0,
  slideId: "",
});

export const writingShapeListAtom = atom<string[]>([]);

export const currentSlideQuestionsNumberAtom = atom<number>(0);

// 学习报告显示状态
export const showLearningReportAtom = atom<boolean>(false);

// 学习报告数据
export const learningReportDataAtom = atom<SlideSummary | null>(null);
